const writeViaTextArea = (text: string): boolean => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '-9999px';
  document.body.appendChild(textArea);

  try {
    textArea.select();
    return document.execCommand('copy');
  } finally {
    // `execCommand` can throw; without this the node would stay in the DOM.
    document.body.removeChild(textArea);
  }
};

const writeText = async (text: string): Promise<boolean> => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    return writeViaTextArea(text);
  } catch {
    return false;
  }
};

/**
 * Copies text to the clipboard, falling back to `execCommand` where the async
 * Clipboard API isn't available.
 *
 * Accepts a **pending** value as well as a resolved one. Safari revokes the
 * user activation a clipboard write needs as soon as the handler awaits
 * anything, so a copy that depends on a network call has to hand the unresolved
 * promise to `ClipboardItem` instead of awaiting it first
 * (https://bugs.webkit.org/show_bug.cgi?id=222262). Picsart's own export sheet
 * uses the same workaround.
 */
const copyToClipboard = async (
  text: string | Promise<string>
): Promise<boolean> => {
  if (typeof text === 'string') return writeText(text);

  try {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
      return await writeText(await text);
    }

    const blob = text.then((value) => {
      // An empty value would be written as an empty clipboard entry and
      // reported as a successful copy.
      if (!value) throw new Error('Nothing to copy');
      return new Blob([value], { type: 'text/plain' });
    });
    // The write consumes this, but not if the write itself is refused first.
    blob.catch(() => {});

    await navigator.clipboard.write([
      new ClipboardItem({ 'text/plain': blob }),
    ]);
    return true;
  } catch {
    // The promise may have rejected, or the write was refused — try once more
    // with the resolved value, which works wherever activation survived.
    return writeText(await text.catch(() => ''));
  }
};

export default copyToClipboard;
